import { useEffect, useMemo, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import { useFetch } from "components/admin/hooks";
import api, { ApiError } from "api/client";

import GearCard from "layouts/simulator/GearCard";
import PetBasics from "layouts/simulator/PetBasics";
import PetExtras from "layouts/simulator/PetExtras";
import ResultCard from "layouts/simulator/ResultCard";
import GearBrowserDialog from "layouts/simulator/GearBrowserDialog";
import { emptyBuild, gearOf, toRequest } from "layouts/simulator/build";

/**
 * Bộ giả lập chỉ số pet.
 *
 * Pet là một mẫu pet trong cơ sở dữ liệu, không gắn với tài khoản nào — mỗi lần tính là một con
 * pet dựng mới trong RAM của máy chủ, nên không có gì bị ghi lại.
 *
 * Mọi công thức chạy ở máy chủ (`POST /api/admin/simulate/pet` → `Pet.ApplyStats`), đúng hàm mà
 * game gọi mỗi lần pet mặc đồ. Trang này chỉ dựng mô tả build rồi hiện con số trả về, nên sửa
 * một hằng số trong GServer là ở đây đổi theo ngay — không có bản sao công thức nào trong JS.
 */
function Simulator() {
  const meta = useFetch(() => api.get("/api/admin/simulate/meta"), []);
  const tattoos = useFetch(() => api.get("/api/admin/templates/tattoos"), []);
  const achievements = useFetch(() => api.get("/api/admin/templates/achievements"), []);

  const [build, setBuild] = useState(emptyBuild);
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState(null);
  /** Chỉ chào trang bị pet đủ chỉ số mang. Tắt đi để xem trước món còn phải cày thêm điểm. */
  const [onlyWearable, setOnlyWearable] = useState(true);
  /** Loại đang mở trong danh mục trang bị; null là đang đóng. */
  const [browsing, setBrowsing] = useState(null);
  const [tab, setTab] = useState(0);
  const requestId = useRef(0);

  const request = useMemo(() => (build.petTemplateId ? toRequest(build) : null), [build]);

  // Gửi lại mỗi khi build đổi, hoãn một nhịp để kéo thanh cấp không gọi liên tục.
  useEffect(() => {
    if (!request) {
      setResult(null);
      setError(null);
      return undefined;
    }
    const id = ++requestId.current;
    setComputing(true);
    const timer = setTimeout(() => {
      api
        .post("/api/admin/simulate/pet", request)
        .then((res) => {
          if (id === requestId.current) {
            setResult(res);
            setError(null);
          }
        })
        .catch((e) => {
          if (id === requestId.current) {
            setError(e instanceof ApiError ? e.message : "Không tính được chỉ số");
          }
        })
        .finally(() => {
          if (id === requestId.current) setComputing(false);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [request]);

  if (meta.loading || tattoos.loading || achievements.loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" py={10}>
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  if (meta.error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Alert severity="error">{meta.error}</Alert>
        </MDBox>
      </DashboardLayout>
    );
  }

  const m = meta.data;
  const gearResultOf = (type) =>
    result ? result.equips.find((e) => e.type === type) || null : null;

  // Số ô đã dùng của từng nhóm, hiện ngay trên nhãn tab để thấy chỗ nào còn trống
  const equipCount = Object.values(build.equips).filter((g) => g && g.templateId).length;
  const dressCount = [build.wing, build.skin].filter((g) => g && g.templateId).length;
  const extraCount =
    build.tattoos.filter((t) => t.templateId).length +
    (build.titleId > 0 ? 1 : 0) +
    build.passives.filter((p) => p.skillId).length;

  const setGear = (type, gear) =>
    setBuild((prev) => ({ ...prev, equips: { ...prev.equips, [type]: gear } }));

  // Lấy thẳng chỉ số pet mà máy chủ vừa tính (đã gồm điểm tiềm năng), nên bộ lọc dùng ĐÚNG con số
  // mà Pet.MeetsRequirement dùng — không chép lại phép cộng điểm ở phía JS.
  // Cánh và cải trang không có yêu cầu chỉ số nên không lọc.
  const petStats =
    onlyWearable && result
      ? { petStr: result.pet.str, petAgi: result.pet.agi, petInt: result.pet._int }
      : null;

  /** Chỉ số pet cho danh mục trang bị — luôn có khi đã chọn pet, không theo công tắc lọc. */
  const rawPetStats = result
    ? { str: result.pet.str, agi: result.pet.agi, int: result.pet._int }
    : null;

  /** Đặt một món vào đúng ô của nó, giữ nguyên cường hoá / dung hợp / ngọc đang có ở ô đó. */
  const pickTemplate = (type, templateId) => {
    if (type === m.wingType) {
      setBuild((prev) => ({ ...prev, wing: { ...prev.wing, templateId } }));
    } else if (type === m.skinType) {
      setBuild((prev) => ({ ...prev, skin: { ...prev.skin, templateId } }));
    } else {
      setBuild((prev) => ({
        ...prev,
        equips: { ...prev.equips, [type]: { ...gearOf(prev, type), templateId } },
      }));
    }
    setBrowsing(null);
  };

  /**
   * Mặc cả bộ. Giữ nguyên ngọc đang khảm ở từng ô; cường hoá và dung hợp thì lấy theo số người
   * dùng chọn trong modal (bỏ qua nếu modal không gửi, ví dụ khi chỉ chọn một ô).
   */
  const pickTemplates = (rows) => {
    setBuild((prev) => {
      const next = { ...prev, equips: { ...prev.equips } };
      rows.forEach(({ type, templateId, lvl, fusion }) => {
        const upgrades = {};
        if (lvl !== undefined) upgrades.lvl = lvl;
        if (fusion !== undefined) upgrades.fusion = fusion;
        if (type === m.wingType) {
          next.wing = { ...prev.wing, templateId, ...upgrades };
        } else if (type === m.skinType) {
          next.skin = { ...prev.skin, templateId, ...upgrades };
        } else {
          next.equips[type] = { ...gearOf(prev, type), templateId, ...upgrades };
        }
      });
      return next;
    });
    setBrowsing(null);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="simulator" />
        {/* alignItems flex-start: PageCard đặt height 100% của ô lưới, mà Grid mặc định kéo hai
            cột cao bằng nhau — để mặc định thì card "Con pet" bị kéo cao bằng cả bảng chỉ số bên
            phải và để lại một mảng trắng khổng lồ trước các tab. */}
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} xl={7}>
            <PageCard
              title="Con pet"
              subtitle="Chọn pet trong cơ sở dữ liệu rồi chia điểm tiềm năng"
              actions={
                <MDButton
                  size="small"
                  variant="outlined"
                  color="white"
                  onClick={() => setBuild(emptyBuild())}
                >
                  <Icon>restart_alt</Icon>&nbsp;Làm lại
                </MDButton>
              }
            >
              <PetBasics build={build} result={result} meta={m} onChange={setBuild} />
            </PageCard>

            {/* Mỗi nhóm nguồn chỉ số là một tab, kèm số ô đã dùng, để không nhóm nào bị
                chìm dưới màn hình như khi xếp dọc. */}
            <Card sx={{ mt: 3 }}>
              <Tabs
                value={tab}
                onChange={(e, next) => setTab(next)}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label={`Trang bị (${equipCount}/${m.equipSlots.length})`} />
                <Tab label={`Cánh & cải trang (${dressCount}/2)`} />
                <Tab label={`Hình xăm, danh hiệu, bị động (${extraCount})`} />
              </Tabs>
            </Card>

            {tab === 0 && (
              <PageCard
                title="Trang bị"
                color="success"
                subtitle={`Mỗi món có ${
                  m.gemSlots
                } lỗ ngọc, mở dần theo cường hoá +${m.gemSlotUnlockLvl.join(" / +")}`}
                actions={
                  <>
                    <MDButton
                      size="small"
                      variant="outlined"
                      color="white"
                      onClick={() => setBrowsing(m.equipSlots[0].type)}
                    >
                      <Icon>inventory_2</Icon>&nbsp;Xem toàn bộ trang bị
                    </MDButton>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={onlyWearable}
                          onChange={(event) => setOnlyWearable(event.target.checked)}
                        />
                      }
                      label={
                        <MDTypography variant="caption" color="white">
                          Chỉ hiện món pet đeo được
                        </MDTypography>
                      }
                    />
                  </>
                }
              >
                {petStats && (
                  <MDTypography variant="caption" color="text" display="block" mb={2}>
                    {`Đang lọc theo sức ${petStats.petStr} / nhanh ${petStats.petAgi} / trí ${petStats.petInt} — đổi điểm tiềm năng là danh sách đổi theo.`}
                  </MDTypography>
                )}
                <Grid container spacing={2}>
                  {m.equipSlots.map((slot) => (
                    <Grid item xs={12} md={6} key={slot.type}>
                      <GearCard
                        label={slot.label}
                        itemType={slot.type}
                        gear={gearOf(build, slot.type)}
                        result={gearResultOf(slot.type)}
                        meta={m}
                        gemSlotCount={m.gemSlots}
                        petStats={petStats}
                        onChange={(gear) => setGear(slot.type, gear)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </PageCard>
            )}

            {tab === 1 && (
              <PageCard
                title="Cánh và cải trang"
                color="warning"
                subtitle="Không bị kiểm tra chỉ số, nhưng có dòng % nhân vào tổng nên được tính sau cùng"
                actions={
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="white"
                    onClick={() => setBrowsing(m.wingType)}
                  >
                    <Icon>flight</Icon>&nbsp;Xem toàn bộ
                  </MDButton>
                }
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <GearCard
                      label="Cánh"
                      itemType={m.wingType}
                      gear={build.wing}
                      result={result ? result.wing : null}
                      meta={m}
                      onChange={(wing) => setBuild((prev) => ({ ...prev, wing }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <GearCard
                      label="Cải trang"
                      itemType={m.skinType}
                      gear={build.skin}
                      result={result ? result.skin : null}
                      meta={m}
                      onChange={(skin) => setBuild((prev) => ({ ...prev, skin }))}
                    />
                  </Grid>
                </Grid>
              </PageCard>
            )}

            {tab === 2 && (
              <PageCard
                title="Hình xăm, danh hiệu, bị động"
                color="error"
                subtitle="Các nguồn chỉ số không nằm trên ô trang bị"
              >
                <PetExtras
                  build={build}
                  result={result}
                  meta={m}
                  tattoos={tattoos.data || []}
                  achievements={achievements.data || []}
                  onChange={setBuild}
                />
              </PageCard>
            )}
          </Grid>

          {/* Riêng cột này vẫn kéo giãn hết chiều cao hàng, vì "dính khi cuộn" cần một khối chứa
              cao hơn chính nó mới có chỗ mà trượt. */}
          <Grid item xs={12} xl={5} alignSelf="stretch">
            <MDBox sx={{ position: { xl: "sticky" }, top: 16 }}>
              <PageCard
                title="Chỉ số"
                color="dark"
                subtitle="Do GServer tính bằng đúng hàm của game"
              >
                <ResultCard result={result} loading={computing} error={error} />
              </PageCard>
            </MDBox>
          </Grid>
        </Grid>

        <MDTypography variant="caption" color="text" display="block" mt={3}>
          Thay đổi ở đây không ghi vào đâu cả và không liên quan tới tài khoản nào. Muốn sửa chỉ số
          thật của vật phẩm thì dùng trang &quot;Sửa vật phẩm &amp; chỉ số&quot;, muốn sửa hằng số
          cân bằng thì dùng trang &quot;Cân bằng&quot; — sửa xong quay lại đây là thấy ngay con số
          mới.
        </MDTypography>
      </MDBox>
      {browsing !== null && (
        <GearBrowserDialog
          meta={m}
          petStats={rawPetStats}
          defaultType={browsing}
          onClose={() => setBrowsing(null)}
          onPick={pickTemplate}
          onPickAll={pickTemplates}
        />
      )}
      <Footer />
    </DashboardLayout>
  );
}

export default Simulator;
